import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles, Crown, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";

const MAX_POLLS = 20;
const POLL_INTERVAL_MS = 2000;

export function PaymentSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed | error
  const [purchaseType, setPurchaseType] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPurchaseType(data.purchase_type);
        if (data.payment_status === "paid") {
          setStatus("paid");
          qc.invalidateQueries({ queryKey: ["subscription-me"] });
          return;
        }
        if (["expired", "failed", "refunded"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_POLLS) {
          setStatus("error");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId, qc]);

  if (status === "polling") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center space-y-4" data-testid="payment-polling">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <h1 className="font-heading text-xl font-bold">Confirmando tu pago…</h1>
          <p className="text-sm text-muted-foreground">
            Estamos verificando la operación con Stripe. Un momento por favor.
          </p>
        </Card>
      </div>
    );
  }

  if (status === "paid") {
    const isBooster = purchaseType === "booster";
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 to-indigo-500/5">
        <Card className="p-8 max-w-md w-full text-center space-y-4" data-testid="payment-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-500" />
          </div>
          <h1 className="font-heading text-2xl font-bold">
            {isBooster ? "¡Booster activado!" : "¡Bienvenido a Premium!"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isBooster ? (
              <>
                Se han añadido <strong>250 mensajes AI</strong> a tu cuenta. Nunca caducan y se
                consumirán cuando se agoten los mensajes del mes.
              </>
            ) : (
              <>
                Tu suscripción está activa. Disfruta de <strong>vistas ilimitadas</strong>,
                <strong> AI ampliada</strong> y <strong>todas las funciones Premium</strong>.
              </>
            )}
          </p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={() => navigate("/")} data-testid="payment-success-home-btn">
              Ir a mi Home
            </Button>
            <Button variant="outline" onClick={() => navigate("/subscription")} data-testid="payment-success-sub-btn">
              Ver mi suscripción
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="p-8 max-w-md w-full text-center space-y-4" data-testid="payment-issue">
        <div className="mx-auto h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <XCircle className="h-9 w-9 text-destructive" />
        </div>
        <h1 className="font-heading text-xl font-bold">No pudimos confirmar el pago</h1>
        <p className="text-sm text-muted-foreground">
          Si tu tarjeta se cargó pero no ves los cambios, revisa tu email o vuelve a intentarlo en unos minutos.
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <Button onClick={() => navigate("/subscription")}>Volver a suscripción</Button>
          <Button variant="outline" onClick={() => navigate("/")}>Ir a Home</Button>
        </div>
      </Card>
    </div>
  );
}

export function PaymentCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="p-8 max-w-md w-full text-center space-y-4" data-testid="payment-cancel">
        <div className="mx-auto h-16 w-16 rounded-full bg-secondary flex items-center justify-center">
          <Sparkles className="h-9 w-9 text-muted-foreground" />
        </div>
        <h1 className="font-heading text-xl font-bold">Compra cancelada</h1>
        <p className="text-sm text-muted-foreground">
          No se ha realizado ningún cargo. Puedes retomarlo cuando quieras.
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <Button onClick={() => navigate("/subscription")}>Volver a los planes</Button>
          <Button variant="outline" onClick={() => navigate("/")}>Ir a Home</Button>
        </div>
      </Card>
    </div>
  );
}
